import { createSelector } from 'reselect'

import * as SelectorUtils from './utils'
import { PLACEHOLDER_IMG, LIFESTYLE_PLACEHOLDER, FILTER_MODE_MENU, FILTER_MODE_NEW } from '../constants/commons'
import { DefaultTopicPlanState } from '../reducers/topicPlans_reducer'
import { DefaultFilters, DefaultTopicPlanFilteredState } from '../reducers/topics_reducer'

import TopicHelpers from '../classes/TopicHelpers'
import * as TLM from '../constants/tlm'

// Selectors used to reformat the state data into something terse we can plug more directly into the
// UI components.

// -----------------------------------------------------------------------------

// Supporting functions

// -----------------------------------------------------------------------------

function renderSocialMeta (data, smtype, meta){
  if (undefined !== data && data.length) {
    for (var i=0;i<data.length;i++){
      if (data[i].platform_type === smtype){
        if (undefined !== data[i].metrics && data[i].metrics.length){
          for (var j=0;j<data[i].metrics.length;j++){
            if (data[i].metrics[j].metric_type === meta){
              return (data[i].metrics[j].value)
            }
          }
        }
      }
    }
  }
  return '--'
}

function thumbnail (r) {
  if (r.entity_localization_override && r.entity_localization_override.length > 0 && r.entity_localization_override[0].entity_override_image) {
    return r.entity_localization_override[0].entity_override_image
  }
  if (r.entity_override_image) {
    return r.entity_override_image
  }
  if (r.thumbnail) {
    return r.thumbnail
  } else if (r.entity_type === 'Lifestyle') {
    return LIFESTYLE_PLACEHOLDER
  }
  return PLACEHOLDER_IMG
}

function getEntityRank (r, entities) {
  if (r.is_entity) {
    let entity = entities.find(e => e.entity_name === r.name)
    return entity ? entity.index : null
  }
  return null
}

function getEntityRanks (rankedEntities, thresholdEntityTypes) {
  let entities = thresholdEntityTypes ? rankedEntities.filter(e => thresholdEntityTypes.indexOf(e.entity_type) > -1) : rankedEntities
  entities.map((r, i) => {
    r.index = i
  })
  return entities
}

/** Reformat the plan details for use in the table */
function reformatRecommendations (recommendations, rankedEntities) {
  const recos = recommendations.map((recommendation, index) => {
    const r = recommendation.recommendation
    return {
      row_id: recommendation.rank_index ? recommendation.rank_index: index+1,
      topic_id: r.id,
      topic_type: TopicHelpers.topicTypeFromDetails(r),
      topic_subtype: TopicHelpers.topicSubTypeFromDetails(r),
      topic_name: r.is_video ? r.title : r.display_name || r.name,
      topic_search_name: r.is_video ? r.title : r.name,
      thumbnail: thumbnail(r),
      // TBD about creating a link back to Diesel for entities?
      link: r.is_entity ? null : r.is_video ? `https://www.youtube.com/watch?v=${r.platform_assigned_id}` : `https://www.youtube.com/channel/${r.platform_assigned_id}`,
      parent_link: r.is_video ? `https://www.youtube.com/channel/${r.channel_platform_assigned_id}` : null,
      channel_platform_assigned_id: r.channel_platform_assigned_id,
      channel_name: r.channel_name,
      platform_assigned_id: r.platform_assigned_id,
      score: recommendation.score,
      segment: recommendation.segment,
      daily_views: renderSocialMeta(r.platform_profiles, 'Youtube', 'DailyViews'),
      total_subscribers: renderSocialMeta(r.platform_profiles, 'Youtube', 'TotalSubscriptions'),
      insta_followers: renderSocialMeta(r.platform_profiles, 'Instagram', 'TotalFollowers'),
      facebook_likes: renderSocialMeta(r.platform_profiles, 'Facebook', 'TotalLikes'),
      twitter_followers: renderSocialMeta(r.platform_profiles, 'Twitter', 'TotalFollowers'),
      labels: r.labels ? ',' + r.labels.join(',') + ',' : null,
      published_date: r.published_at,
      active_date: r.entity_localization_override && r.entity_localization_override.length > 0 && r.entity_localization_override[0].active_date ? r.entity_localization_override[0].active_date : r.active_date,
      networkLabel: TopicHelpers.topicSubTypeFromDetails(r) === TLM.SHOW ? TopicHelpers.getNetworkLabel(r.labels) : null,
      rank: getEntityRank(r, rankedEntities),
      derivedLabels: TopicHelpers.topicSubTypeFromDetails(r) === TLM.BRAND ? TopicHelpers.getBrandDerivedLabels(r.labels) : null,
      category: TopicHelpers.topicSubTypeFromDetails(r) === TLM.SHOW || TopicHelpers.topicSubTypeFromDetails(r) === TLM.MOVIE ? TopicHelpers.getNetworkLabel(r.labels) : TopicHelpers.topicSubTypeFromDetails(r) === TLM.BRAND ? TopicHelpers.getBrandDerivedLabels(r.labels) : null,
      entity_localization_override: r.entity_localization_override
    }
  })

  return recos
}

// -----------------------------------------------------------------------------

// Topic Plan Selectors

// -----------------------------------------------------------------------------

/* Selector helper that extracts the topic id for the current page
 *
 * This is either a target of a shortcut, or something directly in the URL
 */
const getTopicId = (state, props) => {
  // If we got here via a shortcut, blindly accept the target id since we store topic plans by id
  // only. Assume whomever is triggering us has rendered the proper container (Entity or
  // VideoChannel).
  let topicId = null
  if (props.shortcut && props.shortcut.target_id) {
    topicId = props.shortcut.target_id
  } else {
    topicId = props.match.params.topicId
  }

  return topicId
}

/* Fetches the topics byId state */
const getTopicsById = (state) => {
  return state.topics.byId
}

const makeTopicPlanRootSelector = () => {
  return createSelector(
    [getTopicId, getTopicsById],
    (topicId, topicsById) => {
      return (topicsById[topicId] || {}).plan
    }
  )
}

const makeRankedEntitiesSelector = () => {
  return createSelector(
    [getTopicId, getTopicsById],
    (topicId, topicsById) => {
      return (topicsById[topicId] || {}).metrics
    }
  )
}

const makeTopicIdSelector = () => {
  return createSelector(
    [getTopicId],
    topicId => {
      return topicId
    }
  )
}

/* Return a new function that will create a plan filter selector
 *
 */
const makeTopicPlanFiltersSelector = () => {
  const getTopicPlan = makeTopicPlanRootSelector()
  return createSelector(
    [getTopicPlan],
    (topicPlan) => {
      const filters = (topicPlan || {}).currentFilters || DefaultFilters
      return { filters }
    }
  )
}

const makeTopicPlanSelectedFiltersSelector = () => {
  const getTopicPlan = makeTopicPlanRootSelector()
  return createSelector(
    [getTopicPlan],
    (topicPlan) => {
      const filters = (topicPlan || {}).selectedFilters || {}
      return filters
    }
  )
}

/* Returns a new function that will create the plan selector
 *
 * We do this to work around the fact that selectors have a cache size of 1 so we must
 * create a new selector for each topic plan. This is used in mapStateToProps in the
 * Container to generate a selector.
 */
const makeTopicPlanSelector = () => {
  const getTopicPlan = makeTopicPlanRootSelector()
  const getRanks = makeRankedEntitiesSelector()
  return createSelector(
    [getTopicPlan, getRanks],
    (topicPlan, ranks) => {
      let currentFilters = topicPlan ? topicPlan.currentFilters : DefaultFilters
      let rankedEntities = ranks && ranks.details ? ranks.details.ranked_entities : []
      return topicPlanSelector(topicPlan, currentFilters, rankedEntities)
    }
  )
}

const makeTopicPlanExportSelector = () => {
  const getTopicPlan = makeTopicPlanRootSelector()
  return createSelector(
    [getTopicPlan],
    (topic) => {
      return topic?.export
    }
  )
}

const topicPlanSelector = (topicPlan, currentFilters, rankedEntities) => {
  // Use the plan for the current set of filters if possible. Otherwise fallback to the
  // DefaultFilters and failing that the default state. Also note if we found a plan for the
  // current filters if those filters are not the default filters.
  let plan = { ...DefaultTopicPlanFilteredState }
  let planFilters = DefaultFilters
  if (topicPlan) {
    const currentFiltersPlan = topicPlan[currentFilters]
    const currentModule = topicPlan.module
    const currentModuleFilter = topicPlan[currentModule]
    const currentModuleFilterPlan = topicPlan[currentModuleFilter]
    if (currentFiltersPlan && (!topicPlan.filterSelected)) { //  && (!topicPlan.filterSelected)// || topicPlan.mode === 'reset'
      plan = currentFiltersPlan
      planFilters = currentFilters
    } else {
      if (currentModuleFilterPlan && (topicPlan.module === topicPlan.filteredModule)) { // && topicPlan.mode === 'menu'
        plan = currentModuleFilterPlan
        planFilters = currentModuleFilter
      } else {
        const defaultFiltersPlan = topicPlan[DefaultFilters]
        if (defaultFiltersPlan && topicPlan.mode !== FILTER_MODE_MENU && topicPlan.mode !== FILTER_MODE_NEW) {
          plan = defaultFiltersPlan
          planFilters = DefaultFilters
        }
      }
    }
  }
  // Default states take care of initializing these.
  const currentPage = plan.currentPage
  const endOfResult = plan.endOfResult
  // Set isFetching and error to any value from any page. Kind of messy for some cases, but for
  // now we don't care to distinguish between all of the various edge cases.
  let isFetching = false
  let error = null
  // Now, smash all of the pages' recommendations together
  let details = null
  const recos = { recommendations: [] }
  const pages = Array(currentPage).fill().map((_, index) => index + 1)
  const thresholdEntities = currentFilters && currentFilters._filters.filter(t => t.threshold)
  const thresholdEntityTypes = thresholdEntities.length > 0 ? thresholdEntities.map(e => e.topic_sub_type) : []
  const entityRanks = getEntityRanks(rankedEntities, thresholdEntityTypes)
  pages.forEach(page => {
    // Protect against page not loaded yet, or the page has begun to be loaded, but no details
    // are available yet.
    const pagePlan = plan[page] || { ...DefaultTopicPlanState }
    const pageRecos = (pagePlan.details || {}).recommendations || []
    isFetching = isFetching || SelectorUtils.isFetching(pagePlan)
    error = error || pagePlan.error
    // Remove duplicates. This is only possible because of our strange push-down filtering where we
    // do some filtering on the server and within the server, multiple pages of data may be loaded
    // to get to the target number of results. So we may load pages 1, 2 and 3 to get enough data to
    // return to the client, but the client things we just got data back for page 1. This also means
    // we want to update endOfResult if after adding any page of data we haven't changed the overall
    // count of recommendations. I'm skipping this part for now as it fouls up the normal paging
    // behavior, or at least makes it look like there's no results for awhile until the next page
    // comes in...
    const uniqueIds = new Set()
    const mergedRecos = recos.recommendations.concat(pageRecos)
    const uniqueRecos = mergedRecos.filter(reco => {
      const isNew = !uniqueIds.has(reco.recommendation.id)
      uniqueIds.add(reco.recommendation.id)
      return isNew
    })
    recos.recommendations = uniqueRecos
  })

  details = {
    recommendations: reformatRecommendations(recos.recommendations, entityRanks)
  }
  return {
    currentPage,
    endOfResult,
    isFetching,
    planFilters,
    error,
    details: details
  }
}

// -----------------------------------------------------------------------------

// Exports

// -----------------------------------------------------------------------------

export {
  makeTopicIdSelector,
  makeTopicPlanSelector,
  makeTopicPlanFiltersSelector,
  makeTopicPlanSelectedFiltersSelector,
  makeTopicPlanExportSelector
}
